/**
 * Consume ARKO Stream Bridge SSE (fetch + ReadableStream; EventSource cannot set Auth).
 * Events: meta | ttft | token | status | final | error
 */
export type ArkoStreamHandlers = {
  onMeta?: (data: Record<string, unknown>) => void;
  onToken?: (text: string) => void;
  onStatus?: (data: Record<string, unknown>) => void;
  onFinal?: (payload: unknown, timings?: Record<string, unknown>) => void;
  onError?: (code: string, detail?: string) => void;
};

export async function consumeArkoSse(
  url: string,
  headers: Record<string, string>,
  signal: AbortSignal | undefined,
  handlers: ArkoStreamHandlers,
): Promise<void> {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      ...headers,
    },
    signal,
  });
  if (!response.ok) {
    handlers.onError?.('http_' + response.status, String(response.status));
    return;
  }
  if (!response.body) {
    handlers.onError?.('no_body');
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let eventName = 'message';
  let dataLines: string[] = [];

  const safeCall = (fn: () => void) => {
    try {
      fn();
    } catch {
      // Handler-Fehler dürfen den SSE-Loop / die Portal-Route nicht crashen.
      try {
        handlers.onError?.('handler_throw');
      } catch {
        /* ignore nested */
      }
    }
  };

  const flush = () => {
    if (!dataLines.length) {
      eventName = 'message';
      return;
    }
    const raw = dataLines.join('\n');
    dataLines = [];
    let data: Record<string, unknown> = {};
    try {
      data = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      safeCall(() => handlers.onError?.('bad_json'));
      eventName = 'message';
      return;
    }
    if (eventName === 'meta') safeCall(() => handlers.onMeta?.(data));
    else if (eventName === 'token') safeCall(() => handlers.onToken?.(String(data.text || '')));
    else if (eventName === 'status') safeCall(() => handlers.onStatus?.(data));
    else if (eventName === 'ttft') safeCall(() => handlers.onStatus?.(data));
    else if (eventName === 'final') {
      safeCall(() =>
        handlers.onFinal?.(data.payload, (data.timings as Record<string, unknown>) || undefined),
      );
    } else if (eventName === 'error') {
      safeCall(() =>
        handlers.onError?.(String(data.code || 'stream_error'), String(data.detail || '')),
      );
    }
    eventName = 'message';
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n')) >= 0) {
      let line = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 1);
      if (line.endsWith('\r')) line = line.slice(0, -1);
      if (line === '') {
        flush();
        continue;
      }
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
  }
  flush();
}
